import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { IAdvertisement } from '../../interfaces/advertisement.interface';
import { catchError, map, Observable, of, tap, finalize } from 'rxjs';
import { CacheService } from '../caches/cache.service';

@Injectable({
    providedIn: 'root'
})
export class AdsService {
    #http: HttpClient = inject(HttpClient);
    #cacheService: CacheService = inject(CacheService);
    #apiBaseUrl: string = environment.apiUrl;

    #ads: WritableSignal<IAdvertisement[]> = signal<IAdvertisement[]>([]);
    #loading: WritableSignal<boolean> = signal<boolean>(false);

    ads: Signal<IAdvertisement[]> = this.#ads.asReadonly();
    isLoading: Signal<boolean> = this.#loading.asReadonly();

    /**
     * Fetch all advertisements from API with caching
     */
    getAds(forceRefresh: boolean = false): Observable<IAdvertisement[]> {
        const cacheKey = 'all_ads';

        if (forceRefresh) {
            this.#cacheService.invalidate(cacheKey);
        }

        this.#loading.set(true);

        return this.#cacheService.getOrFetch<IAdvertisement[]>(
            cacheKey,
            () => this.#http.get<IAdvertisement[]>(`${this.#apiBaseUrl}/annonces/all`),
            {
                ttl: 15 * 60 * 1000, // 15 minutes cache for ads
                persistToStorage: true
            }
        ).pipe(
            tap((ads: IAdvertisement[]) => {
                this.#ads.set(ads);
            }),
            catchError(() => {
                this.#ads.set([]);
                return of([]);
            }),
            finalize(() => this.#loading.set(false))
        );
    }
}
